import { defineStore } from 'pinia';

import type { Adjustment } from '@stuut/api-sdk/models';
import { waitFor } from '@stuut/common/src/utils';

import { sdk } from '@/sdk';

interface State {
  adjustments: Adjustment[];
  hasAllAdjustments: boolean; // tracks if we have all adjustments or just active ones
  loading: boolean;
}

export const useAdjustmentsStore = defineStore('adjustments', {
  actions: {
    async createAdjustment(adjustment: Adjustment) {
      const adjustments = [adjustment, ...this.adjustments];
      this.$patch({ adjustments });
      return adjustment;
    },

    async deleteAdjustment(adjustmentId: string) {
      const adjustments = this.adjustments.filter((adj) => adj.id !== adjustmentId);
      this.$patch({ adjustments });
      return adjustments;
    },

    async fetchAdjustments(force = false) {
      try {
        if (!force && (this.$state.adjustments.length || this.$state.loading)) {
          await waitFor(() => !this.$state.loading, 250);
        } else {
          this.$patch({ loading: true });
          const response = await sdk.adjustments.list({ isActive: true });
          this.$patch({
            adjustments: response.data ?? [],
            hasAllAdjustments: false,
            loading: false,
          });
        }

        return this.$state.adjustments;
      } catch (error: any) {
        console.error(error);
        this.$patch({ loading: false });
        throw error;
      }
    },

    async fetchAllAdjustments(force = false) {
      try {
        if (
          !force &&
          this.$state.hasAllAdjustments &&
          (this.$state.adjustments.length || this.$state.loading)
        ) {
          await waitFor(() => !this.$state.loading, 250);
        } else {
          this.$patch({ loading: true });
          const response = await sdk.adjustments.list();
          this.$patch({
            adjustments: response.data ?? [],
            hasAllAdjustments: true,
            loading: false,
          });
        }

        return this.$state.adjustments;
      } catch (error: any) {
        console.error(error);
        this.$patch({ loading: false });
        throw error;
      }
    },

    async refetch() {
      return this.fetchAdjustments(true);
    },

    async updateAdjustment(updatedAdjustment: Adjustment) {
      const adjustments = this.adjustments.map((adj) =>
        adj.id === updatedAdjustment.id ? updatedAdjustment : adj,
      );
      this.$patch({ adjustments });
      return updatedAdjustment;
    },
  },

  getters: {
    activeAdjustments: (state) => {
      return state.adjustments.filter((adj) => adj.isActive);
    },

    getById: (state) => {
      return (id: string) => state.adjustments.find((adj) => adj.id === id);
    },

    inactiveAdjustments: (state) => {
      return state.adjustments.filter((adj) => !adj.isActive);
    },
  },

  state: (): State => ({
    adjustments: [],
    hasAllAdjustments: false,
    loading: false,
  }),
});

export default useAdjustmentsStore;
